import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { repoAPI } from './chatApi';
import { RepoType } from '@quyva/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFolder,
    faDatabase,
    faGlobe,
    faSignOutAlt,
    faSearch,
    faUsers,
    faSort,
    faTable,
    faTableColumns,
} from '@fortawesome/free-solid-svg-icons';

const RepoList = () => {
    const [masterAgentRepos, setMasterAgentRepos] = useState([]);
    const [globalRepos, setGlobalRepos] = useState([]);
    const [normalRepos, setNormalRepos] = useState([]);
    const [sharedRepos, setSharedRepos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortAlphabetically, setSortAlphabetically] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchRepos();
    }, []);

    const fetchRepos = async () => {
        try {
            setLoading(true);
            setError('');

            const data = await repoAPI.getAllRepos(user.email, user.token);

            // Master Agent repos (shown first)
            const normalMasterAgents =
                data.Normal?.filter(
                    (item) => item.repotype === RepoType.MASTER_AGENT
                ) || [];
            const sharedMasterAgents =
                data.Shared?.filter(
                    (item) => item.repotype === RepoType.MASTER_AGENT
                ) || [];
            const masterAgents = [...normalMasterAgents, ...sharedMasterAgents];

            // Global repos (shown second)
            const globals =
                data.Normal?.filter(
                    (item) => item.repotype === RepoType.GLOBAL
                ) || [];

            // Normal repos (shown third) - exclude virtual repos
            const normals =
                data.Normal?.filter(
                    (item) =>
                        item.repotype !== RepoType.GLOBAL &&
                        item.repotype !== RepoType.MASTER_AGENT &&
                        item.repotype !== RepoType.VIRTUAL
                ) || [];

            // Shared repos (shown last) - exclude virtual repos
            const shareds =
                data.Shared?.filter(
                    (item) =>
                        item.repotype !== RepoType.MASTER_AGENT &&
                        item.repotype !== RepoType.VIRTUAL
                ) || [];

            setMasterAgentRepos(masterAgents);
            setGlobalRepos(globals);
            setNormalRepos(normals);
            setSharedRepos(shareds);
        } catch (err) {
            console.error('Error fetching repos:', err);
            setError('Failed to load repositories. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRepoClick = (repo) => {
        navigate(`/chatapp/chat/${repo.repoid}`, { state: { repo } });
    };

    const handleLogout = async () => {
        await logout();

        navigate('/');
    };

    const sortRepos = (repoList) => {
        if (sortAlphabetically) {
            return [...repoList].sort((a, b) => a.name.localeCompare(b.name));
        }
        return [...repoList].sort((a, b) => b.repoid - a.repoid); // Latest first
    };

    const filterRepos = (repoList) => {
        return repoList.filter(
            (repo) =>
                repo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (repo.foldername &&
                    repo.foldername
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()))
        );
    };

    const filteredMasterAgents = sortRepos(filterRepos(masterAgentRepos));
    const filteredGlobals = sortRepos(filterRepos(globalRepos));
    const filteredNormals = sortRepos(filterRepos(normalRepos));
    const filteredShareds = sortRepos(filterRepos(sharedRepos));

    const allFilteredRepos = [
        ...filteredMasterAgents,
        ...filteredGlobals,
        ...filteredNormals,
        ...filteredShareds,
    ];

    const getRepoIcon = (repotype, storageType) => {
        if (storageType === RepoType.EXTRACTED_DB) return faTableColumns;
        if (storageType === RepoType.AUTO_DB) return faTable;
        if (repotype === RepoType.MASTER_AGENT) return faUsers;
        if (repotype === RepoType.GLOBAL) return faFolder;
        if (
            [RepoType.SQLLITE, RepoType.POSTGRESS, RepoType.MSSQL].includes(
                repotype
            )
        )
            return faDatabase;
        return faFolder;
    };

    const toggleSort = () => {
        setSortAlphabetically(!sortAlphabetically);
    };

    const totalRepos =
        masterAgentRepos.length +
        globalRepos.length +
        normalRepos.length +
        sharedRepos.length;

    return (
        <div className='min-h-screen bg-backgrounds-secondary flex flex-col'>
            {/* Header */}
            <header className='bg-backgrounds-white border-b border-borders-secondary200 sticky top-0 z-10'>
                <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4'>
                    <div className='flex items-center justify-between'>
                        {/* Logo and Title */}
                        <div className='flex items-center'>
                            <img
                                src='/QuyvaLogo.png'
                                alt='Quyva Logo'
                                className='w-10 h-10 mr-3'
                            />
                            <div>
                                <h2 className='text-2xl sm:text-3xl font-bold text-texts-secondary'>
                                    Quyva{' '}
                                    <span className='text-sm font-normal text-texts-secondary300'>
                                        v1.0.3.2
                                    </span>
                                </h2>
                                <p className='text-xs text-texts-secondary300'>
                                    {' '}
                                    MTree Software (P) Ltd.
                                </p>
                            </div>
                        </div>

                        {/* User Profile and Logout */}
                        <div className='flex items-center gap-4'>
                            <div className='hidden sm:flex items-center'>
                                <div className='w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold mr-3'>
                                    {user?.name
                                        ? user.name.charAt(0).toUpperCase()
                                        : user?.email
                                              ?.charAt(0)
                                              .toUpperCase() || 'U'}
                                </div>
                                <div>
                                    <p className='text-sm font-semibold text-texts-secondary'>
                                        {user?.name ||
                                            user?.email?.split('@')[0] ||
                                            'User'}
                                    </p>
                                    <p className='text-xs text-texts-secondary300'>
                                        {user?.email}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleLogout}
                                className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
                                title='Logout'
                            >
                                <FontAwesomeIcon
                                    icon={faSignOutAlt}
                                    className='text-gray-600'
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className='flex-1 max-w-4xl mx-auto w-full'>
                {/* Search and Sort Bar */}
                <div className='bg-backgrounds-white border-b border-borders-secondary200 px-4 py-3'>
                    <div className='flex items-center gap-3 mb-3'>
                        <div className='relative flex-1'>
                            <FontAwesomeIcon
                                icon={faSearch}
                                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'
                            />
                            <input
                                type='text'
                                placeholder='Search repositories...'
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className='w-full pl-10 pr-3 py-2 bg-backgrounds-secondary border border-borders-secondary200 rounded-lg focus:outline-none focus:ring-2 focus:ring-backgrounds-primary400 focus:border-transparent text-texts-secondary text-sm'
                            />
                        </div>
                        <button
                            onClick={toggleSort}
                            className='flex items-center gap-2 px-3 py-2 bg-backgrounds-primary100 border border-backgrounds-primary200 rounded-lg hover:bg-backgrounds-primary200 transition-colors shadow-sm'
                            title={
                                sortAlphabetically
                                    ? 'Sort by latest'
                                    : 'Sort alphabetically'
                            }
                        >
                            <FontAwesomeIcon
                                icon={faSort}
                                className='text-backgrounds-primary400 text-sm'
                            />
                            <span className='text-xs font-medium text-texts-secondary'>
                                {sortAlphabetically ? 'A-Z' : 'Latest'}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Repository List */}
                {loading ? (
                    <div className='flex justify-center items-center h-full py-20'>
                        <div className='text-center'>
                            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-backgrounds-primary400 mx-auto mb-4'></div>
                            <p className='text-texts-secondary300'>
                                Loading repositories...
                            </p>
                        </div>
                    </div>
                ) : error ? (
                    <div className='flex justify-center items-center h-full py-20'>
                        <div className='bg-backgrounds-white border border-borders-secondary200 rounded-lg p-6 max-w-md mx-auto shadow-lg'>
                            <p className='text-red-600 mb-4 font-medium'>
                                {error}
                            </p>
                            <button
                                onClick={fetchRepos}
                                className='px-4 py-2 bg-backgrounds-primary400 text-white rounded-lg hover:bg-backgrounds-primary500 transition-colors'
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                ) : allFilteredRepos.length === 0 ? (
                    <div className='text-center py-20 px-4'>
                        <svg
                            className='w-16 h-16 mx-auto text-gray-300 mb-3'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={1.5}
                                d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z'
                            />
                        </svg>
                        <h3 className='text-lg font-semibold text-texts-secondary mb-1'>
                            {searchTerm
                                ? 'No repositories found'
                                : 'No repositories available'}
                        </h3>
                        <p className='text-sm text-texts-secondary300'>
                            {searchTerm
                                ? 'Try adjusting your search terms'
                                : 'Contact your administrator to add repositories'}
                        </p>
                    </div>
                ) : (
                    <div className='divide-y divide-borders-secondary200'>
                        {allFilteredRepos.map((repo) => (
                            <div
                                key={repo.repoid}
                                onClick={() => handleRepoClick(repo)}
                                className='bg-backgrounds-white hover:bg-backgrounds-primary100 transition-colors cursor-pointer'
                            >
                                <div className='flex items-center px-4 py-3'>
                                    <div className='w-12 h-12 rounded-full bg-backgrounds-primary100 flex items-center justify-center mr-3 flex-shrink-0'>
                                        <FontAwesomeIcon
                                            icon={getRepoIcon(
                                                repo.repotype,
                                                repo.storage_type
                                            )}
                                            className='text-backgrounds-primary400 text-lg'
                                        />
                                    </div>
                                    <div className='flex-1 min-w-0'>
                                        <div className='flex items-center justify-between'>
                                            <h3 className='text-base font-semibold text-texts-secondary truncate'>
                                                {repo.name}
                                            </h3>
                                            {repo.email !== user.email && (
                                                <span className='text-xs px-2 py-0.5 bg-backgrounds-primary100 text-backgrounds-primary400 rounded ml-2 flex-shrink-0'>
                                                    Shared
                                                </span>
                                            )}
                                        </div>
                                        {repo.foldername && (
                                            <p className='text-sm text-texts-secondary300 truncate mt-0.5'>
                                                {repo.foldername}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Repository Count */}
                {!loading && !error && allFilteredRepos.length > 0 && (
                    <div className='bg-backgrounds-white border-t border-borders-secondary200 px-4 py-2 text-center text-xs text-texts-secondary300'>
                        {allFilteredRepos.length} of {totalRepos}{' '}
                        {totalRepos === 1 ? 'repository' : 'repositories'}
                        {searchTerm && ' (filtered)'}
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className='bg-backgrounds-white border-t border-borders-secondary200 mt-auto'>
                <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4'>
                    <p className='text-xs text-texts-secondary300 text-center'>
                        Powered by MTree Software (P) Ltd.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default RepoList;
